import styled from 'styled-components';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useCustomDataRedirect } from "../common/useCustomDataRedirect";
import AnalyticsBreadcrumb from "ProtoType/NewAnalytics/AnalyticsBreadcrumb";
import Filters from "../Filters";
import { filter_data } from "reports/Tag/calculations";
import ZoneBar from './ZoneBar';
import LongitudinalZoneChart from './LongitudinalZoneChart';

// Colors for zones if not provided by style
const DEFAULT_ZONE_COLORS = [
    '#2D70E2', // blue
    '#6B7280', // gray
    '#1D9A7D', // teal
    '#F59E0B', // amber
    '#EF4444', // red
    '#8B5CF6', // violet
];

const CustomAnalytics = ({
    core_data,
    data,
    rule,
}) => {
    // Redirect protection for custom data from NewAnalytics
    useCustomDataRedirect();

    const selectedFilters = useSelector(
        (state) => state.audit?.new_filters,
        shallowEqual
    );
    const { raw_data } = useSelector(
        (state) => ({ raw_data: state.audit?.raw_data }),
        shallowEqual
    );
    const [detailContext, setDetailContext] = useState(null); // { type: 'org' } | { type: 'anchor', anchorId }
    const hasCustomData = raw_data && Array.isArray(raw_data) && raw_data.length > 0 && raw_data[0]?.datasetMetadata;

    const [correctedData, setCorrectedData] = useState(null);
    const [selectedAnchorCategoryId, setSelectedAnchorCategoryId] = useState(null);

    // Prepare filtered data (respecting existing Filters component behavior)
    useEffect(() => {
        if (!data) return;
        let filteredData = [];

        if (Array.isArray(data) && data.length === 0 && selectedFilters.length === 0) {
            return;
        }

        if (Array.isArray(data) && data.length === 0) {
            setCorrectedData([]);
            return;
        }

        if (data?.[0]?.[0]) {
            filteredData = filter_data(data[0], selectedFilters);
            setCorrectedData(filteredData);
            return;
        }

        filteredData = filter_data(data, selectedFilters);
        setCorrectedData(filteredData);
    }, [data, selectedFilters]);

    // Prefer questions from custom dataset if present, else fall back to core_data.questions
    const questionsSource = useMemo(() => {
        if (hasCustomData && raw_data?.[0]?.question) return raw_data[0].question;
        return core_data?.questions;
    }, [hasCustomData, raw_data, core_data]);

    // Read analytics schema from preferred questions source
    const analyticsSchema = questionsSource?.analytics;
    console.log(core_data)

    // Build mapping from global question id -> {dimensionIndex, factorIndex, questionIndex, reverse}
    const questionIdToPath = useMemo(() => {
        const map = new Map();
        const dims = questionsSource?.dimensions || [];
        dims.forEach((dimension, dimIdx) => {
            (dimension?.factors || []).forEach((factor, facIdx) => {
                (factor?.questions || []).forEach((q, qIdx) => {
                    if (q && q.id != null) {
                        map.set(Number(q.id), {
                            dimensionIndex: dimIdx,
                            factorIndex: facIdx,
                            questionIndex: qIdx,
                            reverse: !!q.reverse,
                        });
                    }
                });
            });
        });
        return map;
    }, [questionsSource?.dimensions]);

    const normalizationByDimensionKey = useMemo(() => {
        const cfg = {};
        (analyticsSchema?.dimensions || []).forEach(d => {
            cfg[d.key] = {
                min: Number(d?.scale?.min ?? 0),
                max: Number(d?.scale?.max ?? 100),
                target: d?.scale?.target || 'high',
                aggregation: d?.aggregation?.method || 'mean',
                missing: d?.aggregation?.missing || 'ignore',
                questionIds: d?.question_ids || [],
            };
        });
        return cfg;
    }, [analyticsSchema]);

    const analysisDef = analyticsSchema?.analyses;

    const allZones = useMemo(() => {
        if (!analysisDef?.zones) return [];
        return analysisDef.zones.map((z, idx) => ({
            id: z.id || `zone_${idx + 1}`,
            label: z.label?.en || `Zone ${idx + 1}`,
            rules: Array.isArray(z.rules) ? z.rules : [],
            priority: Number(z.priority ?? 0),
            style: z.style || {},
        }));
    }, [analysisDef]);

    // Sort zones from lowest priority -> highest
    const sortedZones = useMemo(() => {
        return [...allZones].sort((a, b) => Number(a.priority) - Number(b.priority));
    }, [allZones]);

    // Default zone is always the lowest-priority zone (fallback)
    const lowestPriorityZoneId = useMemo(() => sortedZones?.[0]?.id || 'unclassified', [sortedZones]);

    // Build anchor category options
    const anchorCategories = useMemo(() => {
        const cats = core_data?.categories?.[0]?.categories || [];
        if (!cats.length) return [];
        const nonPrimary = cats.filter(c => (c?.priority || '').toLowerCase() !== 'primary');
        const primary = cats.find(c => (c?.priority || '').toLowerCase() === 'primary');
        const list = [];
        if (primary) {
            list.push({ id: primary.id, name: 'Overview' });
        }
        nonPrimary.forEach(c => list.push({ id: c.id, name: c.name }));
        return list;
    }, [core_data?.categories]);

    useEffect(() => {
        if (!selectedAnchorCategoryId && anchorCategories?.[0]) {
            setSelectedAnchorCategoryId(anchorCategories[0].id);
        }
    }, [anchorCategories, selectedAnchorCategoryId]);

    const anchorOptionsForSelectedCategory = useMemo(() => {
        if (!selectedAnchorCategoryId) return [];
        const cat = core_data?.categories?.[0]?.categories?.find(c => String(c.id) === String(selectedAnchorCategoryId));
        return cat?.options || [];
    }, [core_data?.categories, selectedAnchorCategoryId]);

    const minResponses = useMemo(() => Number(core_data?.Whitelabel?.rule ?? rule ?? 2), [core_data?.Whitelabel?.rule, rule]);

    // Determine which anchor categories have at least one option with sufficient data
    const validAnchorCategories = useMemo(() => {
        if (!correctedData || !anchorCategories?.length) return anchorCategories || [];
        return anchorCategories.filter(cat => {
            const catDef = core_data?.categories?.[0]?.categories?.find(c => String(c.id) === String(cat.id));
            const options = catDef?.options || [];
            // category is valid if any option has respondents > minResponses
            return options.some(opt => {
                const respondents = correctedData.filter(r => {
                    const match = r?.categories?.find(c => String(c.id) === String(cat.id));
                    return match && String(match.response) === String(opt.id);
                });
                return respondents.length > minResponses;
            });
        });
    }, [anchorCategories, correctedData, core_data?.categories, minResponses]);

    // Ensure selected anchor is valid; if not, set to first valid
    useEffect(() => {
        if (!validAnchorCategories?.length) return;
        const isSelectedValid = validAnchorCategories.some(a => a.id === selectedAnchorCategoryId);
        if (!isSelectedValid) {
            setSelectedAnchorCategoryId(validAnchorCategories[0].id);
        }
    }, [validAnchorCategories, selectedAnchorCategoryId]);

    // Helper: get raw question response for a respondent by global id
    const getResponseValueByGlobalId = (respondent, questionGlobalId, scaleCfg) => {
        const path = questionIdToPath.get(Number(questionGlobalId));
        if (!path) return null;
        const items = respondent?.questions || [];
        const match = items.find(q => Number(q.id) === path.dimensionIndex && Number(q.factor) === path.factorIndex && Number(q.q) === path.questionIndex);
        let val = match?.response;
        if (val == null || val === '' || isNaN(Number(val))) return null;
        let num = Number(val);
        if (path.reverse) {
            // Invert on the original scale
            const min = Number(scaleCfg?.min ?? 0);
            const max = Number(scaleCfg?.max ?? 10);
            num = (min + max) - num;
        }
        return num;
    };

    const aggregateDimensionForRespondent = (respondent, dimKey) => {
        const cfg = normalizationByDimensionKey[dimKey];
        if (!cfg) return null;
        const values = (cfg.questionIds || []).map(qid => getResponseValueByGlobalId(respondent, qid, cfg)).filter(v => v != null);
        if (values.length === 0) return null;
        const method = (cfg.aggregation || 'mean').toLowerCase();
        let agg = null;
        if (method === 'mean') {
            agg = values.reduce((s, v) => s + v, 0) / values.length;
        } else if (method === 'median') {
            const sorted = [...values].sort((a, b) => a - b);
            const mid = Math.floor(sorted.length / 2);
            agg = sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
        } else {
            // fallback to mean
            agg = values.reduce((s, v) => s + v, 0) / values.length;
        }
        return agg;
    };

    const normalizeToPercent = (value, min, max) => {
        if (value == null || isNaN(Number(value))) return null;
        const clamped = Math.max(min, Math.min(max, Number(value)));
        if (max === min) return 0;
        return ((clamped - min) / (max - min)) * 100;
    };

    const classifyRespondent = (respondent) => {
        if (!analysisDef || !allZones.length) return { zoneId: 'unclassified' };

        // Build normalized dimension map
        const dimMap = {};
        (analyticsSchema?.dimensions || []).forEach(d => {
            const raw = aggregateDimensionForRespondent(respondent, d.key);
            const percent = raw == null ? null : normalizeToPercent(raw, Number(d?.scale?.min ?? 0), Number(d?.scale?.max ?? 10));
            dimMap[d.key] = percent;
        });

        // Evaluate zones and collect all matches (overlaps allowed)
        const matches = [];
        allZones.forEach(z => {
            const ok = (z.rules || []).every(rule => {
                const v = dimMap[rule.dimension];
                if (v == null) return false;
                const threshold = Number(rule.value);
                switch (rule.op) {
                    case '>': return v > threshold;
                    case '>=': return v >= threshold;
                    case '<': return v < threshold;
                    case '<=': return v <= threshold;
                    case '==': return v === threshold;
                    default: return false;
                }
            });
            if (ok) matches.push(z);
        });

        // Fallback to resolution fallback_zone or 'unclassified'
        if (matches.length === 0) {
            const fallback = analysisDef?.resolution?.fallback_zone || 'unclassified';
            return { zoneId: fallback };
        }

        // Choose the highest-priority match (overlaps resolved by priority desc)
        const best = matches.reduce((a, b) => (Number(a?.priority ?? -Infinity) >= Number(b?.priority ?? -Infinity)) ? a : b);
        return { zoneId: best.id };
    };

    const buildZonePalette = () => {
        const palette = {};
        allZones.forEach((z, i) => {
            const c = z?.style?.color || DEFAULT_ZONE_COLORS[i % DEFAULT_ZONE_COLORS.length];
            palette[z.id] = c;
        });
        // unclassified
        palette['unclassified'] = '#B3B3B3';
        return palette;
    };

    const zonePalette = useMemo(buildZonePalette, [allZones]);

    const overallDistribution = useMemo(() => {
        if (!correctedData || correctedData.length <= minResponses) return null;
        const counts = {};
        correctedData.forEach(r => {
            const { zoneId } = classifyRespondent(r);
            counts[zoneId] = (counts[zoneId] || 0) + 1;
        });
        const total = Object.values(counts).reduce((s, v) => s + v, 0);
        if (!total) return null;
        const zoneIds = sortedZones.length
            ? ['unclassified', ...sortedZones.map(z => z.id)]
            : ['unclassified', ...Object.keys(counts).filter(id => id !== 'unclassified')];
        const items = zoneIds.map(zoneId => ({
            id: zoneId,
            label: (sortedZones.find(z => z.id === zoneId) || allZones.find(z => z.id === zoneId))?.label || (zoneId === 'unclassified' ? 'Unclassified' : zoneId),
            percentage: ((counts[zoneId] || 0) / total) * 100,
            count: counts[zoneId] || 0,
            color: zonePalette[zoneId] || '#B3B3B3',
        }));
        return { total, items };
    }, [correctedData, minResponses, allZones, sortedZones, zonePalette]);

    const byAnchorOptionDistribution = useMemo(() => {
        if (!correctedData || !selectedAnchorCategoryId) return [];
        const options = anchorOptionsForSelectedCategory;
        return options.map(opt => {
            const respondents = correctedData.filter(r => {
                const match = r?.categories?.find(c => String(c.id) === String(selectedAnchorCategoryId));
                return match && String(match.response) === String(opt.id);
            });
            if (respondents.length <= minResponses) return { option: opt, distribution: null };
            const counts = {};
            respondents.forEach(r => {
                const { zoneId } = classifyRespondent(r);
                counts[zoneId] = (counts[zoneId] || 0) + 1;
            });
            const total = Object.values(counts).reduce((s, v) => s + v, 0);
            if (!total) return { option: opt, distribution: null };
            const zoneIds = sortedZones.length
                ? ['unclassified', ...sortedZones.map(z => z.id)]
                : ['unclassified', ...Object.keys(counts).filter(id => id !== 'unclassified')];
            const items = zoneIds.map(zoneId => ({
                id: zoneId,
                label: (sortedZones.find(z => z.id === zoneId) || allZones.find(z => z.id === zoneId))?.label || (zoneId === 'unclassified' ? 'Unclassified' : zoneId),
                percentage: ((counts[zoneId] || 0) / total) * 100,
                count: counts[zoneId] || 0,
                color: zonePalette[zoneId] || '#B3B3B3',
            }));
            return { option: opt, distribution: { total, items } };
        });
    }, [correctedData, selectedAnchorCategoryId, anchorOptionsForSelectedCategory, minResponses, allZones, sortedZones, zonePalette]);

    // Rank categories: most in the highest-priority zone first; fallback down priorities
    const rankedByAnchorOptionDistribution = useMemo(() => {
        const highestPriorityZone = [...sortedZones].sort((a, b) => Number(b.priority) - Number(a.priority))[0];
        const topZoneId = highestPriorityZone?.id;
        const pctForZone = (dist, zid) => (dist?.items?.find(i => i.id === zid)?.percentage) || 0;
        return (byAnchorOptionDistribution || [])
            .filter(x => !!x.distribution)
            .slice()
            .sort((a, b) => {
                const diffTop = pctForZone(b.distribution, topZoneId) - pctForZone(a.distribution, topZoneId);
                if (diffTop !== 0) return diffTop; // higher % in highest-priority zone first
                const totalDiff = (b.distribution.total || 0) - (a.distribution.total || 0);
                if (totalDiff !== 0) return totalDiff;
                return String(a.option.name || '').localeCompare(String(b.option.name || ''));
            });
    }, [byAnchorOptionDistribution, sortedZones]);

    // Historical per-date zone distributions for selected detail context (anchor or org)
    const longitudinalForDetail = useMemo(() => {
        if (!raw_data || !detailContext) return null;
        const timepoints = Array.isArray(raw_data) ? raw_data.slice(1) : [];
        if (!timepoints.length) return null;

        const formatDate = (d) => {
            try {
                const dt = new Date(d);
                if (!isNaN(dt.getTime())) return dt.toLocaleDateString();
            } catch (e) {}
            return String(d);
        };

        // Order matters for stacked bars:
        // - First series renders at the bottom of the stack.
        // - We want: Unclassified at the very bottom, then zones from lowest -> highest priority (top).
        const zoneDefs = [{ id: 'unclassified', label: 'Unclassified' }, ...sortedZones];

        const rows = [];
        timepoints.forEach(tp => {
            const srcBase = Array.isArray(tp?.responses) ? tp.responses : [];
            const src = filter_data(srcBase, selectedFilters);
            const filtered = detailContext.type === 'anchor' && detailContext.anchorId
                ? src.filter(r => {
                    const match = r?.categories?.find(c => String(c.id) === String(selectedAnchorCategoryId));
                    return match && String(match.response) === String(detailContext.anchorId);
                })
                : src;
            // Remove dates with no data after filtering
            if (!filtered.length) return;

            const counts = {};
            filtered.forEach(r => {
                const { zoneId } = classifyRespondent(r);
                counts[zoneId] = (counts[zoneId] || 0) + 1;
            });
            const total = Object.values(counts).reduce((s, v) => s + v, 0) || 1;
            const pct = {};
            zoneDefs.forEach(z => { pct[z.id] = ((counts[z.id] || 0) / total) * 100; });
            rows.push({ date: tp?.date, pct });
        });

        if (!rows.length) return null;

        const categories = rows.map(r => formatDate(r.date));
        const series = zoneDefs
            .map(z => ({
                name: z.label,
                id: z.id,
                data: rows.map(r => Math.round((r.pct[z.id] || 0) * 10) / 10),
            }))
            .filter(s => s.data.some(v => v > 0));
        const colors = series.map(s => zonePalette[s.id] || '#B3B3B3');
        return { categories, series, colors };
    }, [raw_data, detailContext, selectedFilters, selectedAnchorCategoryId, classifyRespondent, sortedZones, zonePalette]);

    if (!core_data) {
        return <div>Loading...</div>;
    }

    if (!analyticsSchema || !analysisDef) {
        return (
            <Container>
                <AnalyticsBreadcrumb currentComponent="custom" />
                <HeaderRow>
                    {/* <BlueTitle>Culture Analytics</BlueTitle> */}
                    <Title>
                        <TitleIconWrapper aria-hidden>
                            <QuadrantIcon />
                        </TitleIconWrapper>
                        Custom Analytics
                    </Title>
                </HeaderRow>
                <EmptyState>Analytics schema not found for this survey.</EmptyState>
            </Container>
        );
    }

    const getAnchorNameById = (anchorId) => {
        const cat = core_data?.categories?.[0]?.categories?.find(c => String(c.id) === String(selectedAnchorCategoryId));
        if (!cat) return anchorId;
        const opt = (cat.options || []).find(o => String(o.id) === String(anchorId));
        return opt?.name || anchorId;
    };

    return (
        <Container>
            <AnalyticsBreadcrumb currentComponent="question" />
            <HeaderRow>
                {/* <BlueTitle>Culture Analytics</BlueTitle> */}
                <Title>
                    <TitleIconWrapper aria-hidden>
                        <QuadrantIcon />
                    </TitleIconWrapper>
                    {analysisDef?.name?.en || 'Custom Analytics'}
                </Title>
            </HeaderRow>

            <OptionsContainer>
                <Controls>
                    <ControlGroup>
                        <Label>Category</Label>
                        <Select value={selectedAnchorCategoryId || ''} onChange={(e) => setSelectedAnchorCategoryId(e.target.value)}>
                            {validAnchorCategories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </Select>
                    </ControlGroup>
                </Controls>
            </OptionsContainer>

            <FiltersRow>
                <Filters />
            </FiltersRow>

            {!detailContext && (
            <Content>
                {overallDistribution && (
                    <Section>
                        <SectionHeader onClick={() => setDetailContext({ type: 'org' })} style={{ cursor: 'pointer' }}>
                            <SectionTitle>Organization Overall</SectionTitle>
                            <Legend>
                                {[...sortedZones, { id: 'unclassified', label: 'Unclassified' }]
                                    .filter(z => (overallDistribution.items.find(i => i.id === z.id)?.percentage || 0) > 0)
                                    .map((z) => (
                                        <LegendItem key={z.id}>
                                            <LegendColor style={{ backgroundColor: zonePalette[z.id] }} />
                                            <span>{z.label}</span>
                                        </LegendItem>
                                    ))}
                            </Legend>
                        </SectionHeader>
                        <ZoneBar data={overallDistribution.items} />
                    </Section>
                )}

                <Section>
                    <SectionHeader>
                        <SectionTitle>By {validAnchorCategories.find(a => a.id === selectedAnchorCategoryId)?.name || 'Category'}</SectionTitle>
                    </SectionHeader>
                    <List>
                        {rankedByAnchorOptionDistribution.map(({ option, distribution }) => (
                            <ListItem key={option.id} onClick={() => setDetailContext({ type: 'anchor', anchorId: option.id })} style={{ cursor: 'pointer' }}>
                                <ItemLabel>{option.name}</ItemLabel>
                                <BarWrapper>
                                    <ZoneBar data={distribution.items} />
                                </BarWrapper>
                            </ListItem>
                        ))}
                    </List>
                </Section>
            </Content>
            )}

            {detailContext && (
                <Section>
                    <SectionHeader>
                        <BackButton onClick={() => setDetailContext(null)}>Back</BackButton>
                        <SectionTitle>
                            {detailContext.type === 'org' ? 'Organization Overall' : getAnchorNameById(detailContext.anchorId)}
                        </SectionTitle>
                    </SectionHeader>
                    {longitudinalForDetail && (
                        <LongitudinalZoneChart
                            categories={longitudinalForDetail.categories}
                            series={longitudinalForDetail.series}
                            colors={longitudinalForDetail.colors}
                        />
                    )}
                </Section>
            )}
        </Container>
    );
};

export default CustomAnalytics;

const QuadrantIcon = () => (
    <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <rect
            x="3"
            y="3"
            width="18"
            height="18"
            rx="3.5"
            stroke="#1F2937"
            strokeWidth="1.6"
        />
        <path
            d="M12 3v18M3 12h18"
            stroke="#94a3b8"
            strokeWidth="1.6"
            strokeLinecap="round"
        />
        <circle cx="16.5" cy="7.5" r="1.6" fill="#1F2937" opacity="0.85" />
        <circle cx="7.5" cy="16.5" r="1.6" fill="#1F2937" opacity="0.85" />
    </svg>
);

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    font-family: 'Raleway';
`;

const HeaderRow = styled.div`
    display: flex;
    flex-direction: column;
`;

const BlueTitle = styled.div`
    color: #2D70E2;
    font-size: 14px;
    font-weight: 600;
    margin-bottom: 15px;
    margin-top: -20px;
`;

const Title = styled.div`
    font-size: 28px;
    font-weight: bold;
    margin-bottom: 30px;
    color: #2A3039;
    display: flex;
    align-items: center;
    gap: 10px;
`;

const TitleIconWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    background: #F8F9FA;
    border-radius: 7px;
    color: #1F2937;
`;

const OptionsContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-height: 40px;
    gap: 24px;
`;

const Controls = styled.div`
    display: flex;
    gap: 16px;
`;

const ControlGroup = styled.label`
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 12px;
    color: #6B7280;
`;

const Label = styled.span``;

const Select = styled.select`
    background: white;
    border: 1px solid #e1e5e9;
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 12px;
    color: #2A3039;
`;

const FiltersRow = styled.div`
    display: flex;
    flex-direction: row;
    width: 100%;
    margin-top: 10px;
    margin-bottom: 10px;
`;

const Content = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const Section = styled.div`
    width: 100%;
    padding: 0 4px;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
`;

const SectionTitle = styled.div`
    font-size: 16px;
    font-weight: 600;
    color: #2A3039;
`;

const SectionTitleButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: #2D70E2;
    cursor: pointer;
    text-align: left;
`;

const Legend = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
`;

const LegendItem = styled.div`
    display: flex;
    gap: 6px;
    align-items: center;
    color: #4B5563;
    font-size: 12px;
`;

const LegendColor = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 2px;
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
`;

const ListItem = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 24px;
    padding: 8px 12px;
`;

const ItemLabel = styled.div`
    flex: 0 0 300px;
    color: #2A3039;
    font-size: 12px;
`;

const AnchorButton = styled.button`
    flex: 0 0 300px;
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    color: #2D70E2;
    font-size: 12px;
    text-align: left;
    cursor: pointer;
`;

const BackButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    margin-right: 12px;
    cursor: pointer;
    font-size: 12px;
`;

const BarWrapper = styled.div`
    flex: 1;
    min-width: 300px;
`;

const EmptyState = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 200px;
    padding: 40px;
    color: #6B7280;
`;

const Subtle = styled.div`
    color: #9CA3AF;
    font-size: 12px;
`;
